import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  createCreditBet,
  createTicket,
  getAccount,
  getUserSettings,
  hostBaseUrl,
  queryKeys,
} from '@/api';
import type { BetslipTicket } from '@/domain/betslip';
import {
  PlaceBetFailure,
  buildPlaceBetRequest,
  mapPlaceBetError,
  validatePlaceBet,
  type PlaceBetLockedEvent,
  type PlaceBetMessage,
  type PlaceBetResponse,
} from '@/domain/place-bet';
import { betslipClear, getBetslipTicket } from './betslip-store';
import { refreshCustomPackagesBeforePlace } from './refresh-custom-before-place';

export interface PlaceBetSuccess {
  ticketNumber: string;
  /** Legacy `DD/MM/YYYY HH:mm:ss` timestamp for the success summary. */
  placedAt: string;
  /** Snapshot of the placed slip (`LastTicket`) before clear. */
  ticket: BetslipTicket;
}

/** Format place time like legacy TicketSuccess (`DD/MM/YYYY HH:mm:ss`). */
export function formatTicketSuccessPlacedAt(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 1023px)').matches;
}

function usersApiBase(): string | undefined {
  try {
    return hostBaseUrl('users');
  } catch {
    return undefined;
  }
}

function currencyFromUser(user: ReturnType<typeof getUserSettings>): string {
  return (user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode ?? '').trim();
}

function placeBetResponseFromUnknown(body: unknown): PlaceBetResponse | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  if (record.Success !== false && typeof record.Error !== 'string') return null;

  const locked = record.LockedEvents;
  return {
    Success: false,
    Error: typeof record.Error === 'string' ? record.Error : undefined,
    Limit: typeof record.Limit === 'number' ? record.Limit : undefined,
    TicketNumber:
      typeof record.TicketNumber === 'string' || typeof record.TicketNumber === 'number'
        ? record.TicketNumber
        : undefined,
    LockedEvents: Array.isArray(locked) ? (locked as PlaceBetLockedEvent[]) : null,
  };
}

/**
 * Place the current betslip ticket: validate → build payload → POST create → clear.
 */
export function usePlaceBet() {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  const mutation = useMutation({
    mutationFn: async (): Promise<PlaceBetSuccess> => {
      const user = getUserSettings();
      const account = getAccount();
      const betSettings = user?.BetSettings ?? account?.BetSettings;

      // Refresh stale BB markets + CBAPI combo odds before validating place.
      await refreshCustomPackagesBeforePlace(queryClient, getBetslipTicket());
      const ticket = getBetslipTicket();

      const validation = validatePlaceBet(ticket, betSettings, {
        restrictedCredits: user?.Credits?.RestrictedCredits,
        withdrawableCredits: user?.Credits?.WithdrawableCredits,
        bonusCredits: user?.Credits?.BonusCredits,
        restrictedMinimumQuota:
          typeof account?.RestrictedMinimumQuota === 'number'
            ? account.RestrictedMinimumQuota
            : undefined,
      });
      if (!validation.ok) {
        throw new PlaceBetFailure(validation.message);
      }

      if (!user?.Guid || !account?.Guid) {
        throw new PlaceBetFailure({
          key: 'Login',
          defaultValue: 'Please log in to place a bet',
        });
      }

      const request = buildPlaceBetRequest(ticket, {
        accountGuid: account.Guid,
        currencyCode: currencyFromUser(user),
        language: (user.Language ?? i18n.language ?? 'en').trim() || 'en',
        userGuid: user.Guid,
        options: betSettings?.Options,
        isMobile: isMobileViewport(),
        externalSportRisk: account.ExternalSportRisk,
        uApiBase: usersApiBase(),
      });

      let response: PlaceBetResponse;
      try {
        response = ticket.CreditorNumber
          ? await createCreditBet(request)
          : await createTicket(request);
      } catch (error) {
        if (error instanceof ApiError) {
          const fromBody = placeBetResponseFromUnknown(error.body);
          if (fromBody) throw new PlaceBetFailure(mapPlaceBetError(fromBody));
          throw new PlaceBetFailure({
            key: 'Error',
            defaultValue: error.message,
          });
        }
        throw new PlaceBetFailure({
          key: 'Error',
          defaultValue: 'Unable to place bet',
        });
      }

      if (!response.Success) {
        throw new PlaceBetFailure(mapPlaceBetError(response));
      }

      // Snapshot before `onSuccess` clears the live slip (legacy `LastTicket`).
      const snapshot = structuredClone(ticket) as BetslipTicket;

      return {
        ticketNumber: response.TicketNumber != null ? String(response.TicketNumber) : '',
        placedAt: formatTicketSuccessPlacedAt(),
        ticket: snapshot,
      };
    },
    onSuccess: async () => {
      betslipClear();
      await queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
    },
  });

  const errorMessage: PlaceBetMessage | null =
    mutation.error instanceof PlaceBetFailure ? mutation.error.placeBetMessage : null;

  return {
    placeBet: mutation.mutateAsync,
    isPending: mutation.isPending,
    reset: mutation.reset,
    errorMessage,
    data: mutation.data ?? null,
  };
}
